export type SnakId = 'congress' | 'booth' | 'results';

export interface CampaignSection {
  title: string;
  body?: string;
  bullets?: string[];
}

export interface CampaignStage {
  id: SnakId;
  number: string;
  moment: string;
  name: string;
  verbs: string;
  tagline: string;
  intro: string;
  accent: string;
  device: 'phone' | 'tablet';
  channels: string[];
  sections: CampaignSection[];
  channelGroups?: { label: string; items: string[] }[];
  quote: string;
  feature?: { title: string; body: string; steps?: string[] };
}

export const campaignHero = {
  eyebrow: 'Ferring · ESHRE 2027 · Connected congress campaign',
  title: 'THE IVF PULSE',
  subtitle: 'A conversation that starts before ESHRE — and continues long after.',
  intro:
    'A connected congress campaign that attracts fertility specialists with something valuable, supports meaningful scientific conversations, and turns collective insights into the next reason to engage.',
};

export const campaignStatement = {
  main: 'Every interaction creates a signal. Every signal makes the next conversation more valuable.',
  sub: 'One connected campaign. Multiple channels. Continuous learning.',
};

export const campaignConcept = {
  eyebrow: 'The overall concept',
  title: 'One conversation. Three moments.',
  body: 'Instead of pushing a message, Ferring offers fertility specialists something worth their time: a chance to share their perspective and see how it compares. Each moment hands over to the next — so ESHRE becomes the middle of a conversation, not a one-off encounter.',
  principle: {
    title: 'The Bumblebee Principle',
    body: 'Like a bee moving between flowers, Ferring visits the fertility community, collects perspectives — and carries valuable insights back to every participant. Each interaction enriches the next.',
  },
  pillars: [
    { title: 'Value first', body: 'Every touchpoint gives something back — insight, comparison or science.' },
    { title: 'Connected', body: 'Answers before the congress shape conversations at the booth and after it.' },
    { title: 'Continuous', body: 'Results invite the next question, so the conversation keeps growing.' },
  ],
};

export const campaignStages: CampaignStage[] = [
  {
    id: 'congress',
    number: '01',
    moment: 'Before & during ESHRE',
    name: 'IVF Pulse',
    verbs: 'Attract & learn',
    tagline: 'Have your say. Discover how your thinking compares.',
    intro:
      'A non-promotional, rewarding peer-perspective experience exploring real challenges in ovarian stimulation — built on the principle Promise → Effort → Reward.',
    accent: '#0F9C8C',
    device: 'phone',
    channels: ['Email / CRM', 'WhatsApp', 'Websites', 'Banners', 'QR codes', 'Aisle-breakers'],
    sections: [
      {
        title: 'The strategic idea',
        body: 'Rather than asking HCPs to listen to a promotional message, Ferring offers something interesting in exchange for their attention. Participants answer short clinical questions and immediately discover how their opinions compare with peers.',
      },
      {
        title: 'Why it matters to HCPs',
        bullets: [
          'A genuinely interesting reason to participate — not a sales message',
          'Sixty seconds, one question per screen, instant reward',
          'See how their thinking compares with colleagues',
          'Adaptive follow-ups that respond to their own answers',
        ],
      },
      {
        title: 'What Ferring gains',
        bullets: [
          'Valuable aggregated perspectives on real clinical priorities',
          'Reach far beyond the HCPs who visit the booth',
          'A relevant, warm opening for the next conversation',
          'A reason to reconnect after the congress',
        ],
      },
    ],
    channelGroups: [
      { label: 'Before ESHRE', items: ['Email / CRM', 'WhatsApp, where permitted', 'Existing websites', 'Digital banners and invitations'] },
      { label: 'During ESHRE', items: ['QR codes on leaflets and congress materials', 'Signage and mobile access', 'Staff-led aisle-breakers', 'Self-guided participation'] },
    ],
    feature: {
      title: 'The Bumblebee Principle',
      body: 'Like a bee moving between flowers, Ferring visits the fertility community, collects perspectives — and carries valuable insights back to every participant. Each interaction enriches the next.',
    },
    quote: 'If we can’t get every HCP to the booth, let’s bring the congress experience to them.',
  },
  {
    id: 'booth',
    number: '02',
    moment: 'At the Ferring booth',
    name: 'REKOVELLE Booth',
    verbs: 'Engage & convince',
    tagline: 'Every patient has a different starting point.',
    intro:
      'A bespoke interactive scientific experience on iPad, designed for two ways of engaging — rep-guided or self-guided.',
    accent: '#5468D8',
    device: 'tablet',
    channels: ['Rep-guided iPad', 'Self-guided stand', 'Future IVA'],
    sections: [
      {
        title: 'The strategic idea',
        body: 'Instead of a linear product presentation, the HCP chooses the scientific subject that interests them most. Every chapter opens with a clinical question, invites a decision and progressively reveals the science.',
      },
      {
        title: 'Rep-guided',
        body: 'The representative uses the iPad to open a scientific dialogue. The HCP selects a relevant topic, makes decisions, explores evidence and discusses the results. Optional rep prompts support the conversation at every step.',
      },
      {
        title: 'Self-guided',
        body: 'For HCPs who prefer to explore independently, or don’t want to commit immediately to a conversation. The same experience runs on a booth iPad — chapter by chapter, at their own pace.',
      },
      {
        title: 'Why it matters to HCPs',
        bullets: [
          'They choose the topic — relevance from the first tap',
          'Interactive, visual science rather than slides',
          'Engage with or without a representative',
        ],
      },
      {
        title: 'What Ferring gains',
        bullets: [
          'Deeper, more consistent scientific dialogue at the booth',
          'Short interactive chapters on approved REKOVELLE scientific and practical topics',
          'A format that may subsequently be adapted for sales-force IVA and other approved channels',
        ],
      },
    ],
    quote: 'From product presentation to scientific dialogue — guided or self-guided.',
  },
  {
    id: 'results',
    number: '03',
    moment: 'After ESHRE',
    name: 'IVF Pulse Results',
    verbs: 'Reconnect & learn',
    tagline: 'You shared your perspective. Here’s the bigger picture.',
    intro:
      'After ESHRE, Ferring reconnects with participating HCPs by giving something valuable back — and asking the next question.',
    accent: '#7A5AC8',
    device: 'phone',
    channels: ['Email', 'WhatsApp', 'Websites', 'Rep follow-up'],
    sections: [
      {
        title: 'The strategic idea',
        body: 'The follow-up Results Snak reveals where specialists agreed, where their perspectives differed, differences between relevant respondent groups, and what the collective results suggest may deserve further exploration.',
      },
      {
        title: 'It doesn’t stop at results',
        body: 'It asks the next question: “Why do you think your colleagues answered this way?” — creating another layer of insight and another rewarding interaction.',
      },
      {
        title: 'Why it matters to HCPs',
        bullets: [
          'Something valuable in return for their participation',
          'A clear view of where the community aligns — and where it doesn’t',
          'A chance to interpret the findings, not just read them',
        ],
      },
      {
        title: 'What Ferring gains',
        bullets: [
          'A welcome, value-led reason to reconnect after the congress',
          'A second layer of insight: why, not just what',
          'An opt-in audience for the next edition',
        ],
      },
      {
        title: 'How it is activated',
        bullets: ['Approved email', 'WhatsApp, where permitted', 'Websites', 'Sales-representative follow-up'],
      },
    ],
    feature: {
      title: 'The long-term opportunity',
      body: 'The IVF Pulse could become a recurring Ferring-sponsored initiative. The conversation keeps growing.',
      steps: ['Each edition explores a relevant clinical question', 'Each round generates perspectives', 'Each follow-up shares findings', 'And invites further interpretation'],
    },
    quote: 'Why do you think your colleagues answered this way?',
  },
];
